import { useState } from 'react';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    category: '',
    message: ''
  });
  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors: {[key: string]: string} = {};

    if (!formData.name.trim()) {
      newErrors.name = 'お名前を入力してください';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'メールアドレスを入力してください';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = '有効なメールアドレスを入力してください';
    }

    if (!formData.category) {
      newErrors.category = 'お問い合わせ種別を選択してください';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'お問い合わせ内容を入力してください';
    } else if (formData.message.length > 500) {
      newErrors.message = 'お問い合わせ内容は500文字以内で入力してください';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('category', formData.category);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d6h90o6cjgvlppf405jg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitSuccess(true);
        setFormData({
          name: '',
          email: '',
          category: '',
          message: ''
        });
        setTimeout(() => {
          setSubmitSuccess(false);
        }, 5000);
      } else {
        alert('送信に失敗しました。もう一度お試しください。');
      }
    } catch (error) {
      alert('送信中にエラーが発生しました。もう一度お試しください。');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <main className="pt-24 pb-20">
        {/* Breadcrumb */}
        <div className="max-w-4xl mx-auto px-6 mb-8">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <a href="/" className="hover:text-pink-500 transition-colors">TOP</a>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-gray-900">お問い合わせ</span>
          </div>
        </div>

        {/* Header */}
        <div className="max-w-4xl mx-auto px-6 mb-12 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            お問い合わせ
          </h1>
          <p className="text-lg text-gray-600 leading-relaxed">
            ご質問やご不明な点がございましたら、お気軽にお問い合わせください。
          </p>
        </div>

        {/* Contact Form */}
        <div className="max-w-3xl mx-auto px-6">
          <div className="bg-gradient-to-br from-pink-50 to-rose-50 rounded-3xl p-8 md:p-12 shadow-lg">
            {submitSuccess && (
              <div className="mb-8 p-6 bg-green-50 border-2 border-green-200 rounded-2xl">
                <div className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-3xl text-green-500 flex-shrink-0"></i>
                  <div>
                    <h3 className="text-xl font-bold text-green-800 mb-2">送信完了</h3>
                    <p className="text-green-700 leading-relaxed">
                      お問い合わせありがとうございます。内容を確認次第、ご連絡させていただきます。
                    </p>
                  </div>
                </div>
              </div>
            )}

            <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-8">
              {/* Name Field */}
              <div>
                <label htmlFor="name" className="block text-lg font-semibold text-gray-900 mb-3">
                  お名前 <span className="text-pink-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`w-full px-6 py-4 text-lg border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-500 transition-all ${
                    errors.name ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-white'
                  }`}
                  placeholder="山田 太郎"
                />
                {errors.name && (
                  <p className="mt-2 text-red-600 text-base flex items-center gap-2">
                    <i className="ri-error-warning-fill"></i>
                    {errors.name}
                  </p>
                )}
              </div>

              {/* Email Field */}
              <div>
                <label htmlFor="email" className="block text-lg font-semibold text-gray-900 mb-3">
                  メールアドレス <span className="text-pink-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`w-full px-6 py-4 text-lg border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-500 transition-all ${
                    errors.email ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-white'
                  }`}
                  placeholder="example@email.com"
                />
                {errors.email && (
                  <p className="mt-2 text-red-600 text-base flex items-center gap-2">
                    <i className="ri-error-warning-fill"></i>
                    {errors.email}
                  </p>
                )}
              </div>

              {/* Category Field */}
              <div>
                <label htmlFor="category" className="block text-lg font-semibold text-gray-900 mb-3">
                  お問い合わせ種別 <span className="text-pink-500">*</span>
                </label>
                <select
                  id="category"
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  className={`w-full px-6 py-4 text-lg border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-500 transition-all cursor-pointer ${
                    errors.category ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-white'
                  }`}
                >
                  <option value="">選択してください</option>
                  <option value="ロッキングについて">ロッキングについて</option>
                  <option value="リワードについて">リワードについて</option>
                  <option value="セキュリティについて">セキュリティについて</option>
                  <option value="技術的な質問">技術的な質問</option>
                  <option value="コミュニティについて">コミュニティについて</option>
                  <option value="その他">その他</option>
                </select>
                {errors.category && (
                  <p className="mt-2 text-red-600 text-base flex items-center gap-2">
                    <i className="ri-error-warning-fill"></i>
                    {errors.category}
                  </p>
                )}
              </div>

              {/* Message Field */}
              <div>
                <label htmlFor="message" className="block text-lg font-semibold text-gray-900 mb-3">
                  お問い合わせ内容 <span className="text-pink-500">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={8}
                  maxLength={500}
                  className={`w-full px-6 py-4 text-lg border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-500 transition-all resize-none ${
                    errors.message ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-white'
                  }`}
                  placeholder="お問い合わせ内容をご記入ください"
                ></textarea>
                <div className="mt-2 flex justify-between items-center">
                  {errors.message ? (
                    <p className="text-red-600 text-base flex items-center gap-2">
                      <i className="ri-error-warning-fill"></i>
                      {errors.message}
                    </p>
                  ) : (
                    <p className="text-gray-500 text-base">
                      {formData.message.length} / 500文字
                    </p>
                  )}
                </div>
              </div>

              {/* Submit Button */}
              <div className="pt-4">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-pink-500 to-rose-500 text-white text-xl font-bold py-5 px-8 rounded-xl hover:from-pink-600 hover:to-rose-600 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center gap-3">
                      <i className="ri-loader-4-line animate-spin text-2xl"></i>
                      送信中...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-3">
                      <i className="ri-send-plane-fill text-2xl"></i>
                      送信する
                    </span>
                  )}
                </button>
              </div>
            </form>

            {/* Note */}
            <div className="mt-8 p-6 bg-white/70 rounded-xl border-2 border-pink-200">
              <div className="flex items-start gap-3">
                <i className="ri-information-fill text-2xl text-pink-500 flex-shrink-0 mt-1"></i>
                <div className="text-gray-700 leading-relaxed">
                  <p className="font-semibold mb-2">ご注意</p>
                  <ul className="space-y-1 text-base">
                    <li>• 通常、2〜3営業日以内にご返信いたします</li>
                    <li>• お急ぎの場合は、コミュニティでもご質問いただけます</li>
                    <li>• 迷惑メールフォルダもご確認ください</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Additional Info */}
          <div className="mt-12 grid md:grid-cols-2 gap-6">
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 text-center">
              <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-community-fill text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">コミュニティで質問</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                他のメンバーと交流しながら疑問を解決できます
              </p>
              <a
                href="/community"
                className="inline-block bg-blue-500 text-white font-bold py-3 px-8 rounded-xl hover:bg-blue-600 transition-all whitespace-nowrap cursor-pointer"
              >
                コミュニティへ
              </a>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-8 text-center">
              <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-book-open-fill text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">初心者講座を見る</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                基本的な疑問は初心者講座で解決できます
              </p>
              <a
                href="/beginner"
                className="inline-block bg-green-500 text-white font-bold py-3 px-8 rounded-xl hover:bg-green-600 transition-all whitespace-nowrap cursor-pointer"
              >
                講座を見る
              </a>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}